<script setup lang="ts">
import { onMounted, useTemplateRef } from 'vue';

const props = defineProps<{
  modelValue: string;
  accessibleLabel: string;
  acceptsDraft: (value: string) => boolean;
}>();
const emit = defineEmits<{
  'update:modelValue': [value: string];
  commit: [];
  cancel: [];
  'commit:keyboard': [];
}>();
const input = useTemplateRef<HTMLInputElement>('input');
let selection: [number, number] = [0, 0];

const rememberSelection = (event: Event): void => {
  const element = event.target as HTMLInputElement;
  if (element.value === props.modelValue) selection = [element.selectionStart ?? 0, element.selectionEnd ?? 0];
};
const handleBeforeInput = (event: Event): void => {
  const inputEvent = event as InputEvent;
  const element = event.target as HTMLInputElement;
  const start = element.selectionStart ?? 0;
  const end = element.selectionEnd ?? start;
  rememberSelection(event);
  if (inputEvent.isComposing || inputEvent.data == null || !inputEvent.inputType.startsWith('insert')) return;
  const next = element.value.slice(0, start) + inputEvent.data + element.value.slice(end);
  if (!props.acceptsDraft(next)) event.preventDefault();
};
const handleInput = (event: Event): void => {
  if ((event as InputEvent).isComposing) return;
  const element = event.target as HTMLInputElement;
  if (props.acceptsDraft(element.value)) {
    emit('update:modelValue', element.value);
    return;
  }
  // Paste, autofill and some keyboards bypass beforeinput.
  element.value = props.modelValue;
  element.setSelectionRange(...selection);
};
const handleEnter = (event: KeyboardEvent): void => {
  if (event.isComposing) return;
  event.preventDefault();
  emit('commit:keyboard');
};

const handleEscape = (event: KeyboardEvent): void => {
  if (event.isComposing) return;
  event.preventDefault();
  emit('cancel');
};

onMounted(() => {
  input.value?.focus();
  input.value?.select();
});
</script>

<template>
  <input
    ref="input"
    :value="modelValue"
    class="input-text rounded-sm bg-surface-primary text-center font-semibold text-tint"
    inputmode="decimal"
    :aria-label="accessibleLabel"
    @beforeinput="handleBeforeInput"
    @select="rememberSelection"
    @input="handleInput"
    @compositionend="handleInput"
    @keydown.enter.stop="handleEnter"
    @keydown.esc.stop="handleEscape"
    @blur="emit('commit')"
  />
</template>
